// src/components/public/ContactForm.js
import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { saveContactMessage } from "../../lib/firestore";

const FIELDS_TR = [
  { key: "name",    label: "Ad Soyad",          type: "text",     placeholder: "Adınız Soyadınız",        required: true,  autoComplete: "name" },
  { key: "email",   label: "E-posta",            type: "email",    placeholder: "ornek@sirket.com",        required: true,  autoComplete: "email" },
  { key: "company", label: "Şirket / Marka",     type: "text",     placeholder: "Şirket adı (opsiyonel)",  required: false, autoComplete: "organization" },
  { key: "budget",  label: "Bütçe Aralığı",      type: "select",   required: false, autoComplete: "off",
    options: ["Belirtmek istemiyorum","₺5.000 – ₺15.000","₺15.000 – ₺30.000","₺30.000 – ₺60.000","₺60.000+"] },
  { key: "message", label: "Proje Detayı",       type: "textarea", placeholder: "Projeniz hakkında kısaca bilgi verin…", required: true, autoComplete: "off" },
];
const FIELDS_EN = [
  { key: "name",    label: "Full Name",          type: "text",     placeholder: "Your Full Name",          required: true,  autoComplete: "name" },
  { key: "email",   label: "Email",              type: "email",    placeholder: "you@company.com",         required: true,  autoComplete: "email" },
  { key: "company", label: "Company / Brand",   type: "text",     placeholder: "Company name (optional)", required: false, autoComplete: "organization" },
  { key: "budget",  label: "Budget Range",       type: "select",   required: false, autoComplete: "off",
    options: ["Prefer not to say","$500 – $1.500","$1.500 – $3.000","$3.000 – $6.000","$6.000+"] },
  { key: "message", label: "Project Details",   type: "textarea", placeholder: "Tell me briefly about your project…", required: true, autoComplete: "off" },
];

export default function ContactForm({ settings }) {
  const { lang } = useLang();
  const fields = lang === "tr" ? FIELDS_TR : FIELDS_EN;
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    fields.forEach(f => {
      if (f.required && !form[f.key]?.trim()) e[f.key] = lang === "tr" ? "Bu alan zorunludur" : "This field is required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = lang === "tr" ? "Geçerli bir e-posta girin" : "Enter a valid email";
    return e;
  };

  const sendEmail = async () => {
    const { emailjs_service_id, emailjs_template_id, emailjs_public_key } = settings || {};
    if (!emailjs_service_id || !emailjs_template_id || !emailjs_public_key) return;

    // Dynamically load EmailJS to avoid bundle bloat
    if (!window.emailjs) {
      await new Promise((res, rej) => {
        const s = document.createElement("script");
        s.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
        s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
      window.emailjs.init(emailjs_public_key);
    }

    await window.emailjs.send(emailjs_service_id, emailjs_template_id, {
      from_name: form.name,
      from_email: form.email,
      company: form.company || "—",
      budget: form.budget || "—",
      message: form.message,
      reply_to: form.email,
    });
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setStatus("sending");
    try {
      await saveContactMessage({ ...form, lang });
      await sendEmail().catch(() => {}); // mail fail → still show success (message saved)
      setStatus("success");
      setForm({ name: "", email: "", company: "", budget: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const inp = { background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", color: "var(--text)", padding: "12px 16px", fontSize: 13.5, fontFamily: "'Inter',sans-serif", width: "100%", outline: "none", transition: "border-color 0.2s, box-shadow 0.2s", borderRadius: "var(--radius-sm)" };
  const errInp = { ...inp, borderColor: "#f26d6d" };

  if (status === "success") return (
    <div style={{ border: "1px solid rgba(61,220,132,0.28)", background: "rgba(61,220,132,0.07)", borderRadius: "var(--radius-lg)", padding: "48px 40px", textAlign: "center", backdropFilter: "blur(10px)" }}>
      <div style={{ fontSize: 32, marginBottom: 16 }}>✓</div>
      <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 20, marginBottom: 12, color: "var(--success)" }}>
        {lang === "tr" ? "Mesajınız alındı!" : "Message received!"}
      </div>
      <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.8, margin: "0 0 24px" }}>
        {lang === "tr" ? "En kısa sürede size dönüş yapacağım." : "I'll get back to you as soon as possible."}
      </p>
      <button onClick={() => setStatus("idle")} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", borderRadius: 999, color: "var(--text)", padding: "9px 22px", fontSize: 12, cursor: "pointer" }}>
        {lang === "tr" ? "Yeni mesaj" : "New message"}
      </button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="form-grid-responsive" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {fields.map(f => {
          const hasErr = !!errors[f.key];
          const style = hasErr ? errInp : inp;
          const fieldId = `contact-${f.key}`;
          const errId = `${fieldId}-error`;
          const a11y = {
            id: fieldId,
            name: f.key,
            autoComplete: f.autoComplete,
            "aria-required": f.required || undefined,
            "aria-invalid": hasErr ? "true" : undefined,
            "aria-describedby": hasErr ? errId : undefined,
          };
          return (
            <div key={f.key} style={{ gridColumn: (f.type === "textarea" || f.key === "message") ? "1/-1" : "auto" }}>
              <label htmlFor={fieldId} style={{ display: "block", fontSize: 12, fontWeight: 500, color: hasErr ? "#f26d6d" : "var(--muted)", marginBottom: 8 }}>
                {f.label}{f.required && <span style={{ color: "var(--accent)", marginLeft: 3 }}>*</span>}
              </label>
              {f.type === "select" ? (
                <select {...a11y} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ ...style, cursor: "pointer" }}>
                  <option value="">{lang === "tr" ? "Seçiniz…" : "Select…"}</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <textarea {...a11y} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} rows={5} style={{ ...style, resize: "vertical" }}
                  onFocus={e => e.target.style.borderColor = "var(--accent)"}
                  onBlur={e => e.target.style.borderColor = hasErr ? "#f26d6d" : "var(--border)"}
                />
              ) : (
                <input {...a11y} type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} style={style}
                  onFocus={e => e.target.style.borderColor = "var(--accent)"}
                  onBlur={e => e.target.style.borderColor = hasErr ? "#f26d6d" : "var(--border)"}
                />
              )}
              {hasErr && <div id={errId} role="alert" style={{ fontSize: 11, color: "#f26d6d", marginTop: 5 }}>{errors[f.key]}</div>}
            </div>
          );
        })}
      </div>

      {status === "error" && (
        <div role="alert" style={{ background: "rgba(242,109,109,0.1)", border: "1px solid rgba(242,109,109,0.4)", borderRadius: "var(--radius-sm)", color: "#f26d6d", padding: "12px 16px", fontSize: 12.5, marginTop: 16 }}>
          {lang === "tr" ? "Bir hata oluştu, lütfen tekrar deneyin." : "Something went wrong, please try again."}
        </div>
      )}

      <div style={{ marginTop: 24, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
        <button type="submit" disabled={status === "sending"}
          style={{ background: status === "sending" ? "rgba(255,255,255,0.08)" : "var(--gradient)", color: status === "sending" ? "var(--muted)" : "#0a0a12", border: "none", borderRadius: 999, padding: "14px 34px", fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 14, cursor: status === "sending" ? "not-allowed" : "pointer", boxShadow: status === "sending" ? "none" : "0 8px 24px -8px rgba(124,108,255,0.55)", transition: "transform 0.2s, box-shadow 0.2s" }}
          onMouseEnter={e => { if (status !== "sending") { e.currentTarget.style.transform = "translateY(-2px)"; } }}
          onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}
        >
          {status === "sending"
            ? (lang === "tr" ? "Gönderiliyor…" : "Sending…")
            : (lang === "tr" ? "Gönder →" : "Send →")}
        </button>
        <span style={{ fontSize: 11, color: "var(--muted)" }}>
          {lang === "tr" ? "* Zorunlu alanlar" : "* Required fields"}
        </span>
      </div>
    </form>
  );
}
